
import { Accordion } from "@/components/ui/accordion";
import { useState } from "react";
import { AgeLevelSection } from "@/components/testing/AgeLevelSection";
import { getAllCategories } from "@/utils/categoryMapping";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useTests } from "@/hooks/useTests";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getLevelsForAgeAndCategory, getTestByLevelAndCategory } from "@/utils/testUtils";
import { AGE_LEVELS } from "@/constants/testLevels";

const CATEGORIES = getAllCategories().map(name => ({ name }));

interface TestingModulesSectionProps {
  childId: string;
}

export const TestingModulesSection = ({ childId }: TestingModulesSectionProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const { data: tests, isLoading: testsLoading } = useTests();
  
  // Fetch test completion status for this child
  const { data: completionStatus } = useQuery({
    queryKey: ["testCompletionStatus", childId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("test_completion_status")
        .select("*")
        .eq("child_id", childId);

      if (error) {
        console.error("Error fetching test completion status:", error);
        throw error;
      }

      return data || [];
    },
    enabled: !!childId
  });

  const handleCategoryClick = (categoryId: string) => {
    setOpenCategory(openCategory === categoryId ? null : categoryId);
  };

  const isTestCompleted = (testId: string) => {
    return completionStatus?.some(status => 
      status.test_id === testId && status.completed
    );
  };

  const handleLevelClick = async (age: string, category: string, level: string) => {
    try {
      console.log("Selecting test with params:", { age, category, level });
      localStorage.setItem("selectedChildId", childId);

      const data = await getTestByLevelAndCategory(age, category, level);

      if (!data) {
        console.error("No test found for:", { age, category, level });
        toast({
          title: "Test Not Found",
          description: "The selected test could not be found.",
          variant: "destructive",
        });
        return;
      }

      console.log("Found test:", data);
      navigate(`/test/${data.id}`);
    } catch (error) {
      console.error("Error in handleLevelClick:", error);
      toast({
        title: "Error",
        description: "Failed to load the test. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (testsLoading) {
    return <div className="flex items-center justify-center p-8">Loading tests...</div>;
  }

  return (
    <div className="space-y-4">
      <Accordion type="single" collapsible className="space-y-4">
        {AGE_LEVELS.map((age) => (
          <AgeLevelSection
            key={age}
            age={age}
            categories={CATEGORIES}
            openCategory={openCategory}
            onCategoryClick={handleCategoryClick}
            getLevelsForAgeAndCategory={(age, category) => {
              const levels = getLevelsForAgeAndCategory(tests, age, category);
              return levels.map(level => ({
                ...level,
                completed: isTestCompleted(level.id),
                className: isTestCompleted(level.id) ? "bg-green-200 hover:bg-green-300" : undefined
              }));
            }}
            onLevelClick={handleLevelClick}
          />
        ))}
      </Accordion>
    </div>
  );
};
